import {
  IBasicHeader,
  ICustomModal,
  IFooter2,
  IFooterWithCta,
  IHeaderNavigation,
  ILayout,
  IModal,
  IPaidLandingPageFooter,
  IVariationContainer,
} from 'marketing-site/@types/generated/contentful'
import { BodyPart as IntercomBodyPart } from 'marketing-site/src/components/common/Intercom'
import SkipToContent from 'marketing-site/src/components/common/SkipToContent'
import { HeaderAndFooterBrandProvider } from 'marketing-site/src/library/providers/brandProvider/headerAndFooterBrandProvider'
import { containerMaxWidth, mq } from 'marketing-site/src/library/utils'
import dynamic from 'next/dynamic'
import React, { useEffect, useRef, useState } from 'react'

const ContentfulRooflineBanner = dynamic(
  async () =>
    (await import('marketing-site/src/transformers/components/ContentfulRooflineBanner'))
      .ContentfulRooflineBanner,
)
const ContentfulHeaderNavigation = dynamic(
  async () =>
    (await import('marketing-site/src/transformers/components/ContentfulHeaderNavigation'))
      .ContentfulHeaderNavigation,
)
const ContentfulBasicHeader = dynamic(
  async () =>
    (await import('marketing-site/src/transformers/components/ContentfulBasicHeader'))
      .ContentfulBasicHeader,
)
const ContentfulVariationContainer = dynamic(
  async () =>
    (await import('marketing-site/src/transformers/components/ContentfulVariationContainer'))
      .ContentfulVariationContainer,
)
const ContentfulPaidLandingPageFooter = dynamic(
  async () =>
    (await import('marketing-site/src/transformers/components/ContentfulPaidLandingPageFooter'))
      .ContentfulPaidLandingPageFooter,
)
const ContentfulFooter = dynamic(
  async () =>
    (await import('marketing-site/src/transformers/components/ContentfulFooter')).ContentfulFooter,
)
const ContentfulFooterWithCTA = dynamic(
  async () =>
    (await import('marketing-site/src/transformers/elements/ContentfulFooterWithCTA'))
      .ContentfulFooterWithCTA,
)
const ContentfulModal = dynamic(
  async () =>
    (await import('marketing-site/src/transformers/elements/ContentfulModal')).ContentfulModal,
)

const ContentfulCustomModal = dynamic(
  async () =>
    (await import('marketing-site/src/transformers/elements/ContentfulCustomModal'))
      .ContentfulCustomModal,
)

const ContentfulSecondaryNav = dynamic(
  async () =>
    (await import('marketing-site/src/transformers/components/ContentfulSecondaryNav'))
      .ContentfulSecondaryNav,
)

export interface IProps {
  children: React.ReactNode
  classes?: string
  layout: ILayout
}

function renderHeader(layout: ILayout): React.ReactNode {
  const SecondaryNavComponent = layout.fields.secondaryNavigation ? (
    <ContentfulSecondaryNav {...layout.fields.secondaryNavigation} />
  ) : null

  if (layout.fields.navigation) {
    const headerId = layout.fields.navigation.sys.contentType.sys.id

    if (headerId === 'headerNavigation') {
      return (
        <ContentfulHeaderNavigation
          {...(layout.fields.navigation as IHeaderNavigation)}
          secondaryNav={SecondaryNavComponent}
        />
      )
    } else if (headerId === 'basicHeader') {
      return <ContentfulBasicHeader {...(layout.fields.navigation as IBasicHeader)} />
    } else if (headerId === 'variationContainer') {
      return <ContentfulVariationContainer {...(layout.fields.navigation as IVariationContainer)} />
    }
    // add any other headers here
  }
}

type FooterType = IFooter2 | IPaidLandingPageFooter | IFooterWithCta

function isFooter2(footer: FooterType): footer is IFooter2 {
  return footer.sys.contentType.sys.id === 'footer2'
}

function isPaidLandingPageFooter(footer: FooterType): footer is IPaidLandingPageFooter {
  return footer.sys.contentType.sys.id === 'paidLandingPageFooter'
}

function isFooterWithCTA(footer: FooterType): footer is IFooterWithCta {
  return footer.sys.contentType.sys.id === 'footerWithCTA'
}

function renderFooter(layout: ILayout): React.ReactNode {
  const { footer } = layout.fields
  if (footer) {
    if (isFooter2(footer)) {
      return <ContentfulFooter {...footer} />
    } else if (isPaidLandingPageFooter(footer)) {
      return <ContentfulPaidLandingPageFooter {...footer} />
    } else if (isFooterWithCTA(footer)) {
      return <ContentfulFooterWithCTA {...footer} />
    }

    // add any other footers here
  }
}

function ModalRenderer({ modal }: { modal?: IModal | ICustomModal }) {
  if (!modal) return null
  switch (modal.sys.contentType.sys.id) {
    case 'modal':
      return <ContentfulModal {...(modal as IModal)} />

    case 'customModal':
      return <ContentfulCustomModal {...(modal as ICustomModal)} />

    default:
      return null
  }
}

const Layout: React.FC<IProps> = ({ children, classes, layout }) => {
  const { direction = '' } = layout.fields

  const [numComponents, setNumComponents] = useState<number>(0)
  const mainRef = useRef<HTMLElement>(null)
  useEffect(() => {
    const count = mainRef.current ? mainRef.current.childNodes.length : 0
    setNumComponents(count)
  }, [mainRef])

  return (
    <div id="layout-wrapper" className={classes}>
      {layout.fields.notificationBanner && (
        <ContentfulRooflineBanner {...layout.fields.notificationBanner} />
      )}

      <SkipToContent label={layout.fields.skipToContentLabel} />

      <HeaderAndFooterBrandProvider>{renderHeader(layout)}</HeaderAndFooterBrandProvider>

      <main id="main" tabIndex={-1} className={direction} ref={mainRef}>
        {children}
        <IntercomBodyPart
          disableMessenger={layout.fields.disableMessenger}
          hideLauncher={layout.fields.hideMessenger}
        />
      </main>

      <HeaderAndFooterBrandProvider>{renderFooter(layout)}</HeaderAndFooterBrandProvider>

      <ModalRenderer modal={layout.fields.modal} />

      <style jsx>{`
        .horizontal {
          margin: 0 auto;
          padding: 0 40px 40px;
          max-width: ${containerMaxWidth};

          @media (${mq.laptop}) {
            display: grid;
            grid-template-columns: repeat(${numComponents}, 1fr);
            column-gap: 50px;
          }

          @media (${mq.desktopLg}) {
            padding: 0 0 40px;
          }
        }
      `}</style>
    </div>
  )
}

export default Layout
